<template>
  <table
    v-if="store.digests.length"
    class="digests-table"
    data-product-tour="step-8"
  >
    <thead>
      <tr>
        <th class="name">Nom</th>
        <th>Veille</th>
        <th>Jours</th>
        <th>Heure</th>
        <th>Corpus</th>
        <th class="center state">Active</th>
        <th class="center actions">Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="digest in store.digests" :key="digest._id" class="digest-row">
        <td>
          {{ digest.name }}
        </td>
        <td>
          {{ digest.dashboard_name }}
        </td>
        <td>
          <div class="days">
            <ServiceTooltip
              v-for="(day, index) in days"
              :key="index"
              :text="day.displayName"
            >
              <span
                class="day-icon"
                :class="
                  digest.frequency_days.includes(day.name) ? 'active' : ''
                "
              >
                {{ day.initial }}
              </span>
            </ServiceTooltip>
          </div>
        </td>
        <td>
          {{ digest.frequency_hour.toString().padStart(2, 0) }}h{{
            digest.frequency_minutes.toString().padStart(2, 0)
          }}
        </td>
        <td>
          <div class="services">
            <ServiceTooltip
              v-for="(service, index) in services"
              :key="index"
              :text="service.displayName"
            >
              <span
                class="service-icon"
                :class="
                  digest.alert_services.includes(service.name) ? 'active' : ''
                "
              >
                <i class="fa-lg" :class="service.icon"></i>
              </span>
            </ServiceTooltip>
          </div>
        </td>
        <td>
          <div class="center">
            <SwitchSlide
              :is-active="digest.active"
              @toggle="store.toggleDigest(digest._id)"
              @click="handleOnClick"
            />
          </div>
        </td>
        <td>
          <div class="center actions">
            <ButtonWithPaywall
              rounded
              :override-icon="false"
              size="small"
              color="nocolor"
              variant="noborder"
              :fallback-action="openFreemiumModal"
              @click="() => openUpdateDialog(digest)"
            >
              <i class="fa-solid fa-pen edit"></i>
            </ButtonWithPaywall>
            <ButtonWithPaywall
              rounded
              :override-icon="false"
              size="small"
              color="nocolor"
              variant="noborder"
              :fallback-action="openFreemiumModal"
              @click="openDeleteDialog(digest)"
            >
              <i class="fa-solid fa-trash-can delete"></i>
            </ButtonWithPaywall>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <NoAlert
    v-else
    title="Aucun récapitulatif configuré"
    message="Configurez un récapitulatif pour être averti périodiquement des nouveaux
        éléments pertinents disponibles."
  />

  <PopupDialog
    ref="updateModal"
    title="Éditer un récapitulatif"
    @close="closeDialog"
  >
    <template #body>
      <AddEditDigestForm
        v-if="showForm"
        ref="updateForm"
        :targeted-digest="targetedDigest"
      />
    </template>
    <template #footer>
      <button v-if="showForm" v-focus class="btn-primary" @click="updateDigest">
        Éditer
      </button>
    </template>
  </PopupDialog>
  <PopupDialog
    ref="deleteModal"
    title="Supprimer un récapitulatif"
    @close="targetedDigest = null"
  >
    <template #body>
      <p class="modal-text">
        Êtes-vous sûr de vouloir supprimer le récapitulatif ?
      </p>
      <p class="modal-text">"{{ targetedDigest?.name }}" ?</p>
    </template>
    <template #footer>
      <button v-focus class="btn-delete" @click="deleteDigest">
        Supprimer
      </button>
    </template>
  </PopupDialog>
</template>

<script setup>
import { inject, ref } from "vue";
import SwitchSlide from "@follaw-fr/components/reusable/atoms/SwitchSlide.vue";
import PopupDialog from "@follaw-fr/components/reusable/atoms/PopupDialog.vue";
import ServiceTooltip from "@follaw-fr/components/alerting/atoms/ServiceTooltip.vue";
import NoAlert from "@follaw-fr/components/alerting/atoms/NoAlert.vue";
import AddEditDigestForm from "@follaw-fr/components/alerting/molecules/AddEditDigestForm.vue";
import { useDigestsStore } from "@follaw-fr/stores/digests";
import { openDigestPaywallModalKey } from "@follaw-fr/constants/injection-keys";
import ButtonWithPaywall from "@follaw-fr/components/reusable/molecules/ButtonWithPaywall.vue";
import { usePermissionGuard } from "@follaw-fr/composables/usePermissionGuard";

const { permissionGuard } = usePermissionGuard();

const openFreemiumModal = inject(openDigestPaywallModalKey) ?? undefined;

const store = useDigestsStore();

const services = ref([
  {
    name: "event",
    displayName: "Événements",
    icon: "fa-solid fa-calendar-lines",
  },
  { name: "jorf", displayName: "Jorf", icon: "fa-kit fa-jorf" },
  // {
  //   name: "petition",
  //   displayName: "Pétitions",
  //   icon: "fa-solid fa-file-signature",
  // },
  { name: "news", displayName: "Actualités", icon: "fa-kit fa-communiques" },
  {
    name: "legislation",
    displayName: "Dossiers législatifs",
    icon: "fa-kit fa-dossierlegi",
  },
  { name: "amendment", displayName: "Amendements", icon: "fa-kit fa-am" },
  { name: "question", displayName: "Questions", icon: "fa-kit fa-questions" },
  { name: "compte-rendu", displayName: "Comptes-rendus", icon: "fa-kit fa-cr" },
  {
    name: "social-media",
    displayName: "Publications",
    icon: "fa-solid fa-chart-network",
  },
  {
    name: "transcription",
    displayName: "Transcriptions",
    icon: "fa-solid fa-file-audio",
  },
  // {
  //   name: "articles",
  //   displayName: "Articles",
  //   icon: "fa-kit fa-sv-articles",
  // },
]);

const days = ref([
  {
    initial: "L",
    name: "monday",
    displayName: "Lundi",
  },
  {
    initial: "M",
    name: "tuesday",
    displayName: "Mardi",
  },
  {
    initial: "M",
    name: "wednesday",
    displayName: "Mercredi",
  },
  {
    initial: "J",
    name: "thursday",
    displayName: "Jeudi",
  },
  {
    initial: "V",
    name: "friday",
    displayName: "Vendredi",
  },
  {
    initial: "S",
    name: "saturday",
    displayName: "Samedi",
  },
  {
    initial: "D",
    name: "sunday",
    displayName: "Dimanche",
  },
]);

const targetedDigest = ref(null);

const handleOnClick = (e) =>
  permissionGuard({
    fallBack: () => {
      e.preventDefault();
      openFreemiumModal();
    },
  });

const deleteModal = ref();
const openDeleteDialog = (digest) => {
  targetedDigest.value = digest;
  deleteModal.value?.open();
};
const deleteDigest = () => {
  store.deleteDigest(targetedDigest.value._id);
  deleteModal.value?.close();
};

const updateModal = ref();
const showForm = ref(false);
const openUpdateDialog = (digest) => {
  targetedDigest.value = digest;
  showForm.value = true;
  updateModal.value?.open();
};

const updateForm = ref();
const updateDigest = () => {
  if (updateForm.value?.validate()) {
    showForm.value = false;
    updateForm.value?.form.requestSubmit();
  }
};

const closeDialog = () => {
  targetedDigest.value = null;
  showForm.value = false;
};
</script>

<style lang="scss" scoped>
.digests-table {
  thead {
    color: var(--clr-n8);
    background-color: var(--clr-p2);

    th {
      &.center {
        text-align: center;
      }

      &.state {
        width: 75px;
      }

      &.actions {
        width: 85px;
      }
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: var(--clr-n1);
      }

      &:hover {
        background-color: var(--clr-p1);
      }

      td {
        .center {
          display: flex;
          justify-content: space-around;
        }

        .services,
        .days {
          display: flex;
          gap: 3px;
          font-weight: 400;

          .service-icon,
          .day-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            background-color: var(--clr-p2);

            &.active {
              background-color: var(--clr-p5);
            }
          }
        }

        .actions {
          gap: 5px;
          button {
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: var(--shd-card);
            border-radius: 50%;
            background-color: white;

            &:hover,
            &:focus {
              background: var(--clr-p2);
              box-shadow: var(--shd-card);
            }
            &:active {
              background-color: var(--clr-p1);
            }
          }

          .edit {
            padding: 6px;
            color: var(--clr-s7);
          }

          .delete {
            padding: 6px 7px;
            color: var(--clr-e300);
          }
        }
      }
    }
  }

  .fa-envelope {
    color: var(--clr-p5);
  }
  .fa-telegram {
    color: var(--clr-telegram);
  }
  .fa-slack {
    color: var(--clr-slack);
  }
  .fa-ms-teams {
    color: var(--clr-ms-teams);
  }
}

// Modal contents
.modal-text {
  margin: 0;
  text-align: center;
}
</style>
